import { isNil } from 'ramda';

import { IKnmRequirementViolation } from 'APIPackage/KnmModule/interfaces/IKnmRequirementViolation';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { convertFormDateValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateValueToKnmDataDate';
import { IViolationMaterialTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IViolationMaterialTypeSelectOption';
import { convertViolationFixesFormValuesToKnmData } from 'KnmErknmPackage/MainModule/common/convertViolationFixesFormValuesToKnmData';
import { IKnmString } from 'APIPackage/KnmModule/interfaces/IKnmString';
import { convertKnmDocumentAttachmentFormValueToKnmData } from 'KnmPackage/MainModule/common/convertKnmDocumentAttachmentFormValueToKnmData';

import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import {
  IKnmViolationEvent,
  IKnmViolationEventMaterial,
  IKnmViolationLinkedEvent,
} from '../../../APIPackage/KnmModule/interfaces/IKnmViolatedRequirementsNpa248';
import { IViolationEventSelectOption } from '../../ErknmViolationsModule/common/createPassportEventSelectOptions';
import { IVideoFixationSelectOption } from '../../ErknmViolationsModule/common/createVideoFixationSelectOptions';

const createLinkedEvent = (
  eventOption: IViolationEventSelectOption | null | undefined,
): IKnmViolationLinkedEvent | null => {
  if (!eventOption?.value) {
    return null;
  }

  const { eventType } = eventOption;

  return {
    guid: eventOption.value,
    eventType:
      eventType?.dictId && eventType?.dictVersionId
        ? {
            dictId: eventType.dictId,
            dictVersionId: eventType.dictVersionId,
          }
        : null,
  };
};

export function convertViolationsFormValuesToKnmData(
  formViolations: IKnmErknmFormViolation[],
  isDraft: boolean | undefined,
): IKnmRequirementViolation[] {
  return formViolations.map(violation => {
    const isInEvent = !!violation.inEvent?.value;

    return {
      isNew: isDraft ? violation.isNew : null,
      guid: violation.id || null,
      status: violation.status || null,
      description: violation.title || null,
      descriptionReactQuillEditor: handleReactQuillDeltaValue(
        violation.descriptionReactQuillEditor || violation.title || null,
      ),
      notRemoval: typeof violation.isUnfixable?.value === 'boolean' ? violation.isUnfixable.value : null,
      correctedInEvent: typeof violation.isFixedOnPlace?.value === 'boolean' ? violation.isFixedOnPlace.value : null,
      explanation: violation.explanation,
      explanationReactQuillEditor: handleReactQuillDeltaValue(
        violation.explanationReactQuillEditor || violation.explanation || null,
      ),
      corrected: typeof violation.isFixed?.value === 'boolean' ? violation.isFixed.value : null,
      appealed: typeof violation.isAppealed?.value === 'boolean' ? violation.isAppealed.value : null,
      isViolationResolved:
        typeof violation.isViolationResolved?.value === 'boolean' ? violation.isViolationResolved.value : null,
      isViolationResolvedNote: violation.isViolationResolvedNote || null,
      dateResolvedByAgreement: convertFormDateValueToKnmDataDate(violation.dateResolvedByAgreement),
      previousDateResolvedByAgreement: convertFormDateValueToKnmDataDate(violation.previousDateResolvedByAgreement),

      remediationPlan: {
        guid: violation.fixPlanId || null,
        plannedEliminationDates: violation.fixPlanDates?.length
          ? violation.fixPlanDates
              .map<IKnmString>(dateItem => ({
                isNew: isDraft ? dateItem.isNew : null,
                guid: dateItem.guid,
                dateCreate: dateItem.dateCreate,
                value: convertFormDateValueToKnmDataDate(dateItem.value),
              }))
              .filter(item => !isNil(item.value))
          : null,
        plannedMethod: violation.fixPlanMethods?.length
          ? (violation.fixPlanMethods
              .map(methodItem => methodItem.value)
              .filter(item => !isNil(item)) as IViolationMaterialTypeSelectOption[])
          : null,
        descriptions: violation.fixPlanExplanations?.length
          ? violation.fixPlanExplanations
              .map<IKnmString>(explanationItem => ({
                isNew: isDraft ? explanationItem.isNew : null,
                guid: explanationItem.guid,
                dateCreate: explanationItem.dateCreate,
                value: explanationItem.value,
                valueReactQuillEditor: handleReactQuillDeltaValue(
                  explanationItem.valueReactQuillEditor || explanationItem.value,
                ),
              }))
              .filter(item => !isNil(item.value))
          : null,
      },

      actualEliminationDate: convertFormDateValueToKnmDataDate(violation.fixFactDate),
      actualMethod: violation.fixFactMethod || null,
      eliminationViolations: violation.fixes?.length
        ? convertViolationFixesFormValuesToKnmData(violation.fixes, isDraft)
        : null,
      updated: violation.updated,
      updatedBy: violation.updatedBy,

      includeInSubject: typeof violation.includeInSubject === 'boolean' ? violation.includeInSubject : null,
      prescriptionLink: violation.prescriptionLink || null,
      number: violation.number || null,
      repeatCorrected: typeof violation.repeatCorrected?.value === 'boolean' ? violation.repeatCorrected.value : null,
      repeatCorrectedInEvent:
        typeof violation.repeatCorrectedInEvent?.value === 'boolean' ? violation.repeatCorrectedInEvent.value : null,
      inEvent: typeof violation.inEvent?.value === 'boolean' ? violation.inEvent.value : null,
      repeatExplanation: violation.repeatExplanation,
      targetLink: violation.targetLink || null,
      targetErpId: violation.targetErpId || null,
      resolvedIn: violation.resolvedIn || null,
      events:
        isInEvent && violation.events?.length
          ? violation.events.map(event => {
              const linkedEvent = createLinkedEvent(
                event.eventOption as IViolationEventSelectOption | null | undefined,
              );
              const result: IKnmViolationEvent = {
                guid: event.guid,
                violationId: violation.id,
                event: linkedEvent,
                materials:
                  event.materials?.map(material => {
                    const videoFixations =
                      material.videoFixations
                        ?.filter(item => item.videoFixationOption?.value)
                        .map(item => {
                          const option = item.videoFixationOption as IVideoFixationSelectOption;

                          return {
                            guid: item.guid,
                            videoFixationsGuid: option.value || null,
                            number: option.number || null,
                          };
                        }) || null;

                    const typeDoc = material.typeDocConfirmationViolation;
                    const attachments =
                      material.attachments
                        ?.map(attachmentItem => convertKnmDocumentAttachmentFormValueToKnmData(attachmentItem))
                        .filter((item): item is NonNullable<typeof item> => !!item) || [];
                    const links =
                      material.links
                        ?.map(link => ({
                          id: link.id,
                          value: link.value?.trim() || null,
                        }))
                        .filter(link => !!link.value) || [];

                    const materialResult: IKnmViolationEventMaterial = {
                      guid: material.guid,
                      videoFixations,
                      typeDocConfirmationViolation: typeDoc
                        ? {
                            id: typeDoc.id,
                            code: typeDoc.code,
                            name: typeDoc.name,
                          }
                        : null,
                      attachments: attachments?.length ? attachments : null,
                      links: links?.length ? links : null,
                    };

                    return materialResult;
                  }) || null,
              };

              return result;
            })
          : null,
    };
  });
}
